import { useCallback, useEffect, useState } from 'react'
import { EMPTY_PRODUCT_NAV, parseProductNav, type ProductNavPrefs } from '@/lib/productNav'

const storageKey = (userId: string) => `ss.nav.products.${userId}`

/** The signed-in person's "By product" arrangement, per user in localStorage; other tabs follow along. */
export function useProductNav(userId: string | undefined): [ProductNavPrefs, (prefs: ProductNavPrefs) => void] {
  const read = useCallback(() => {
    if (!userId) return EMPTY_PRODUCT_NAV
    try {
      return parseProductNav(localStorage.getItem(storageKey(userId)))
    } catch {
      return EMPTY_PRODUCT_NAV
    }
  }, [userId])
  const [prefs, setPrefs] = useState<ProductNavPrefs>(read)

  useEffect(() => {
    setPrefs(read())
    if (!userId) return
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(userId)) setPrefs(read())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [read, userId])

  const save = useCallback(
    (next: ProductNavPrefs) => {
      setPrefs(next)
      if (!userId) return
      try {
        localStorage.setItem(storageKey(userId), JSON.stringify(next))
      } catch {
        // Private mode / quota: the arrangement just isn't remembered.
      }
    },
    [userId],
  )
  return [prefs, save]
}
